import { useEffect, useMemo, useState } from "react";
import {
  listCounties,
  listOfficers,
  type County,
  type Officer,
} from "../lib/api";
import {
  PendingOnboardingPage,
  type PendingSignup,
} from "./PendingOnboardingPage";

type Phase = "form" | "onboarding";

interface FormState {
  fullName: string;
  email: string;
  countyId: string; // composite id "state||county" — we resolve back to canonical strings on submit
  officerUid: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function openedFromAppSearch(): boolean {
  return new URL(window.location.href).searchParams.get("from") === "app";
}

export function SignupPage() {
  const [phase, setPhase] = useState<Phase>("form");
  const [counties, setCounties] = useState<County[] | null>(null);
  const [countiesError, setCountiesError] = useState<string | null>(null);
  const [countiesRetry, setCountiesRetry] = useState(0);
  const [officers, setOfficers] = useState<Officer[] | null>(null);
  const [officersLoading, setOfficersLoading] = useState(false);
  const [officersError, setOfficersError] = useState<string | null>(null);
  const [officersRetry, setOfficersRetry] = useState(0);
  const [pendingSignup, setPendingSignup] = useState<PendingSignup | null>(
    null,
  );
  const openedFromApp = useMemo(openedFromAppSearch, []);

  const [form, setForm] = useState<FormState>({
    fullName: "",
    email: "",
    countyId: "",
    officerUid: "",
  });
  // Free-text search for the officer picker
  const [officerSearch, setOfficerSearch] = useState("");
  // Collapses to a compact "selected officer" card after the user picks one.
  // Re-opens via the Change button or when the underlying officer list changes.
  const [officerPickerOpen, setOfficerPickerOpen] = useState(true);

  const selectedCounty = useMemo<County | null>(() => {
    if (!counties || !form.countyId) return null;
    return counties.find((c) => `${c.state}||${c.county}` === form.countyId) ?? null;
  }, [counties, form.countyId]);

  useEffect(() => {
    let cancelled = false;
    setCountiesError(null);
    setCounties(null);
    (async () => {
      try {
        const resp = await listCounties();
        if (!cancelled) setCounties(resp.counties);
      } catch (err) {
        if (!cancelled)
          setCountiesError(String((err as Error)?.message ?? err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countiesRetry]);

  // When county changes, fetch officers for that county and clear the
  // previously selected officer.
  useEffect(() => {
    if (!selectedCounty) {
      setOfficers(null);
      setOfficersError(null);
      setForm((f) => ({ ...f, officerUid: "" }));
      return;
    }
    let cancelled = false;
    setOfficersLoading(true);
    setOfficersError(null);
    setOfficers(null);
    setForm((f) => ({ ...f, officerUid: "" }));
    setOfficerSearch("");
    setOfficerPickerOpen(true);
    (async () => {
      try {
        const resp = await listOfficers(selectedCounty.county);
        if (cancelled) return;
        setOfficers(resp.officers);
      } catch (err) {
        if (!cancelled)
          setOfficersError(String((err as Error)?.message ?? err));
      } finally {
        if (!cancelled) setOfficersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCounty, officersRetry]);

  const filteredOfficers = useMemo(() => {
    if (!officers) return [];
    const q = officerSearch.trim().toLowerCase();
    if (!q) return officers;
    return officers.filter((o) => o.display_name.toLowerCase().includes(q));
  }, [officers, officerSearch]);

  // ---- validation ----
  const errors: string[] = [];
  if (form.fullName.trim().length < 2) errors.push("Full legal name");
  if (!EMAIL_RE.test(form.email.trim().toLowerCase())) errors.push("Valid email");
  if (!selectedCounty) errors.push("County");
  // Officer required only when the filtered list is non-empty for this county.
  const officerRequired = !!officers && officers.length > 0;
  if (officerRequired && !form.officerUid) errors.push("Probation officer");

  const canSubmit = errors.length === 0 && phase === "form";

  function handleSubmit() {
    if (!selectedCounty) return;
    // No backend call — we just capture the form snapshot and hand it to
    // PendingOnboardingPage. The account (Auth user + Firestore doc +
    // Stripe subscription) is not created until payment succeeds.
    const selectedOfficer =
      form.officerUid && officers
        ? (officers.find((o) => o.uid === form.officerUid) ?? null)
        : null;
    setPendingSignup({
      email: form.email.trim().toLowerCase(),
      displayName: form.fullName.trim(),
      county: selectedCounty.county,
      state: selectedCounty.state,
      officer: selectedOfficer
        ? {
            uid: selectedOfficer.uid,
            name: selectedOfficer.display_name,
            phone: null,
            email: null,
          }
        : null,
    });
    setPhase("onboarding");
  }

  if (phase === "onboarding" && pendingSignup) {
    return (
      <PendingOnboardingPage
        pendingSignup={pendingSignup}
        openedFromApp={openedFromApp}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          Start your enrollment
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          We'll walk you through the lease and set up billing. Your account is
          created only after payment, so nothing is charged or committed until
          the very end. The whole process takes about 5 minutes.
        </p>
      </div>

      <Section title="About you">
        <Field label="Full legal name" colSpan="sm:col-span-2">
          <input
            type="text"
            autoComplete="name"
            value={form.fullName}
            onChange={(e) =>
              setForm((f) => ({ ...f, fullName: e.target.value }))
            }
            placeholder="Jane Q. Doe"
            className={inputClass}
          />
        </Field>
        <Field label="Email" colSpan="sm:col-span-2">
          <input
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) =>
              setForm((f) => ({ ...f, email: e.target.value }))
            }
            placeholder="you@example.com"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-slate-500">
            After payment we'll email your sign-in credentials and a copy of
            your signed lease here.
          </p>
        </Field>
      </Section>

      <Section title="Court / supervision">
        <Field label="County" colSpan="sm:col-span-2">
          {countiesError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">
              Failed to load counties: {countiesError}
              <button
                type="button"
                onClick={() => setCountiesRetry((n) => n + 1)}
                className="ml-2 font-medium underline underline-offset-2"
              >
                Retry
              </button>
            </div>
          ) : !counties ? (
            <div className={`${inputClass} text-slate-400`}>Loading…</div>
          ) : (
            <select
              value={form.countyId}
              onChange={(e) =>
                setForm((f) => ({ ...f, countyId: e.target.value }))
              }
              className={inputClass}
            >
              <option value="">Select your county…</option>
              {counties.map((c) => (
                <option key={c.id} value={`${c.state}||${c.county}`}>
                  {c.county}, {c.state}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Probation officer" colSpan="sm:col-span-2">
          {!selectedCounty ? (
            <div className={`${inputClass} text-slate-400`}>
              Select a county first
            </div>
          ) : officersLoading ? (
            <div className={`${inputClass} text-slate-400`}>
              Loading officers…
            </div>
          ) : officersError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">
              Failed to load officers: {officersError}
              <button
                type="button"
                onClick={() => setOfficersRetry((n) => n + 1)}
                className="ml-2 font-medium underline underline-offset-2"
              >
                Retry
              </button>
            </div>
          ) : officers && officers.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No probation officers are listed for {selectedCounty.county}{" "}
              County yet. Please contact AMS support to get your officer added,
              then come back to finish enrollment.
            </div>
          ) : form.officerUid && !officerPickerOpen ? (
            // Compact "selected" card. Tapping Change re-opens the picker.
            (() => {
              const selected = officers?.find(
                (o) => o.uid === form.officerUid,
              );
              if (!selected) return null;
              return (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-emerald-900 truncate">
                      {selected.display_name}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOfficerPickerOpen(true);
                      setOfficerSearch("");
                    }}
                    className="shrink-0 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    Change
                  </button>
                </div>
              );
            })()
          ) : (
            <>
              <input
                type="search"
                placeholder="Search by name…"
                value={officerSearch}
                onChange={(e) => setOfficerSearch(e.target.value)}
                autoFocus
                className={`${inputClass} mb-2`}
              />
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                {filteredOfficers.length === 0 ? (
                  <div className="p-3 text-sm text-slate-500">
                    No matches.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredOfficers.map((o) => (
                      <li key={o.uid}>
                        <button
                          type="button"
                          onClick={() => {
                            setForm((f) => ({ ...f, officerUid: o.uid }));
                            setOfficerPickerOpen(false);
                            setOfficerSearch("");
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${
                            form.officerUid === o.uid
                              ? "bg-brand-50/60 ring-1 ring-inset ring-brand-200"
                              : ""
                          }`}
                        >
                          <div className="text-sm font-medium text-slate-900">
                            {o.display_name}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {form.officerUid ? (
                <button
                  type="button"
                  onClick={() => setOfficerPickerOpen(false)}
                  className="mt-2 text-xs font-medium text-slate-600 hover:text-slate-900"
                >
                  Done
                </button>
              ) : null}
            </>
          )}
        </Field>
      </Section>

      {errors.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Almost there — still need: {errors.join(", ")}.
        </div>
      ) : null}

      <button
        type="button"
        disabled={!canSubmit}
        onClick={handleSubmit}
        className="w-full rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-600 transition"
      >
        Continue
      </button>
    </div>
  );
}

// ----- small primitives (mirror SignStep) -----

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none";

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="mb-4">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  colSpan,
  children,
}: {
  label: string;
  colSpan?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={colSpan ?? ""}>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
